// «Мои промокоды» в профиле веба (fd-promo-34 + PDF «Доработки платформы»
// п.1, 27.10.2026). Раздел профиля: маршрут /profile/coupons, пункт меню
// в components/profile/profile-shell.tsx.
//
// • «Добавить код»: найденный в соцсетях, у блогера или у продавца код
//   сохраняется без применения — шлюз проверяет только, что он есть и ещё
//   действует (POST /buyer/coupons), отказ — текстом шлюза под полем.
// • «Доступные» — карточки-билеты, как в ленте корзины: название, скидка,
//   срок, условия (тексты собирает шлюз). «Применить» ведёт в корзину с
//   `?apply=КОД` (не `?promo=` — тот параметр означает партнёрскую ссылку).
// • «Закончились» — сохранённые коды, которые уже не применить, с причиной
//   («Срок действия кода истёк», «…только для первого заказа на OSMIX»).
//   Истёкшие пропадают сами через 30 дней после срока — фильтр шлюза.
// • «История» — погашения с номером заказа.
//
// Отдельным файлом вне pages/profile/**: адаптив профиля параллельно правит
// другая зона, здесь только своя страница.

import { Skeleton } from "@osmix/ui/skeleton";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { toast } from "sonner";
import { api, type BuyerCoupon, type BuyerCoupons } from "@/api";
import { ProfilePanel, ProfileSegments } from "@/components/profile/profile-shell";
import { CabinetEmpty } from "@/components/profile/return-parts";
import { PromoTicket } from "@/components/promo/promo-ticket";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/text-field";
import { useFormatPrice } from "@/lib/use-format-price";
import { useProfile } from "@/store";
type Tab = "available" | "history";
const SOURCE_LABEL: Record<BuyerCoupon["source"], string> = {
  personal: "Персональный",
  saved: "Сохранённый",
  public: "Для всех"
};
const dateRu = (iso: string) => new Date(iso).toLocaleDateString("ru-RU");
export function MyPromosPage() {
  const userId = useProfile(s => s.user?.id ?? "guest");
  const qc = useQueryClient();
  const navigate = useNavigate();
  const fmt = useFormatPrice();
  const [params, setParams] = useSearchParams();
  const tab: Tab = params.get("tab") === "history" ? "history" : "available";
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const key = ["buyer", userId, "coupons"];
  const coupons = useQuery({
    queryKey: key,
    queryFn: () => api.cabinet.coupons()
  });
  const available = coupons.data?.available ?? [];
  const ended = coupons.data?.ended ?? [];
  const history = coupons.data?.history ?? [];
  const refresh = (data: BuyerCoupons) => qc.setQueryData(key, data);
  const save = async () => {
    const value = code.trim();
    if (!value || busy) return;
    setBusy(true);
    setError(null);
    try {
      refresh(await api.cabinet.saveCoupon(value));
      setCode("");
      toast.success("Промокод добавлен");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Промокод не добавлен");
    } finally {
      setBusy(false);
    }
  };
  const remove = async (c: BuyerCoupon) => {
    setBusy(true);
    try {
      refresh(await api.cabinet.removeCoupon(c.code));
    } catch (e_0) {
      toast.error(e_0 instanceof Error ? e_0.message : "Не удалось удалить промокод");
    } finally {
      setBusy(false);
    }
  };
  const copy = async (value_0: string) => {
    try {
      await navigator.clipboard.writeText(value_0);
      toast.success("Код скопирован");
    } catch {
      // Буфер недоступен — код и так виден на карточке.
    }
  };
  const footer = (c_0: BuyerCoupon) => <div className="flex flex-wrap items-center gap-2">
			<button type="button" onClick={() => void copy(c_0.code)} className="rounded-[8px] border border-dashed border-[#1F1F1F]/24 px-2 py-1 font-mono text-[12px] leading-4 font-semibold text-[#161616] outline-none hover:border-[#FA5C2E] focus-visible:ring-2 focus-visible:ring-osmix-tangelo/40" aria-label={`Скопировать код ${c_0.code}`}>
				{c_0.code}
			</button>
			<span className="text-[12px] leading-4 text-[#1F1F1F]/48">{SOURCE_LABEL[c_0.source]}</span>
			{c_0.source === "saved" && <button type="button" disabled={busy} onClick={() => void remove(c_0)} className="ml-auto text-[12px] leading-4 text-[#1F1F1F]/48 outline-none hover:text-[#1F1F1F] focus-visible:text-[#1F1F1F]" data-testid={`coupon-remove-${c_0.code}`}>
					Удалить
				</button>}
		</div>;
  const grid = "grid grid-cols-[repeat(auto-fill,minmax(238px,1fr))] gap-x-2.5 gap-y-5";
  return <ProfilePanel title="Мои промокоды">
			<ProfileSegments<Tab> value={tab} onChange={v => setParams(v === "available" ? {} : {
      tab: v
    }, {
      replace: true
    })} options={[{
      value: "available",
      label: "Доступные"
    }, {
      value: "history",
      label: "История"
    }]} />

			{tab === "available" && <div className="mb-4 flex flex-col gap-1.5 rounded-[20px] bg-white p-3" data-testid="coupon-save">
					<div className="flex flex-col gap-2 sm:flex-row sm:items-center">
						<Input variant="square" value={code} invalid={Boolean(error)} onChange={e_1 => {
          setCode(e_1.target.value.toUpperCase().replace(/\s/g, ""));
          setError(null);
        }} onKeyDown={e_2 => {
          if (e_2.key === "Enter") void save();
        }} placeholder="Код из соцсетей, от блогера или продавца" aria-label="Промокод" className="font-normal" fieldClassName="flex-1" />
						<Button variant="gray" size="md" disabled={busy || !code.trim()} onClick={() => void save()} data-testid="coupon-save-btn">
							Добавить
						</Button>
					</div>
					{error ? <p role="alert" className="text-[12px] leading-4 text-ds-error" data-testid="coupon-save-error">
							{error}
						</p> : <p className="text-[12px] leading-4 text-[#1F1F1F]/48">Код сохранится здесь - применить его можно в корзине.</p>}
				</div>}

			{coupons.isPending ? <div className={grid}>
					{[0, 1, 2].map(i => <Skeleton key={i} className="h-[126px] rounded-[16px]" />)}
				</div> : coupons.isError ? <CabinetEmpty title="Не удалось загрузить промокоды" hint="Обновите страницу чуть позже." /> : tab === "available" ? <div className="flex flex-col gap-6">
					{available.length === 0 ? <CabinetEmpty title="Доступных промокодов пока нет" hint="Добавьте код выше - здесь же появятся персональные промокоды и акции магазинов." /> : <ul className={grid} data-testid="coupons-available">
							{available.map((c_1, i_0) => <li key={c_1.code} data-testid={`coupon-${c_1.code}`}>
									<PromoTicket className="w-full" coupon={c_1} index={i_0} action={c_1.status === "scheduled" ? {
            label: "Скоро",
            disabled: true
          } : {
            label: "Применить",
            onClick: () => navigate(`/cart?apply=${encodeURIComponent(c_1.code)}`)
          }} note={c_1.status === "scheduled" ? c_1.statusMessage : null}>
										{footer(c_1)}
									</PromoTicket>
								</li>)}
						</ul>}

					{ended.length > 0 && <section className="flex flex-col gap-3" data-testid="coupons-ended">
							<h2 className="text-[16px] leading-[22px] font-semibold text-[#161616]">Закончились</h2>
							<ul className={grid}>
								{ended.map((c_2, i_1) => <li key={c_2.code} data-testid={`coupon-ended-${c_2.code}`}>
										<PromoTicket className="w-full" coupon={c_2} index={i_1} muted note={c_2.statusMessage}>
											{footer(c_2)}
										</PromoTicket>
									</li>)}
							</ul>
						</section>}
				</div> : history.length === 0 ? <CabinetEmpty title="Вы ещё не применяли промокоды" hint="Когда оплатите заказ с промокодом, он появится здесь." /> : <ul className="flex flex-col gap-2.5" data-testid="coupons-history">
					{history.map(h => <li key={`${h.code}-${h.orderId}`} className="flex flex-wrap items-center justify-between gap-3 rounded-[20px] bg-white p-4">
							<div className="min-w-0">
								<p className="font-mono text-[14px] leading-[18px] font-semibold text-[#161616]">{h.code}</p>
								<p className="mt-1 text-[12px] leading-4 text-[#1F1F1F]/64">
									{dateRu(h.usedAt)} ·{" "}
									<Link to={`/profile/orders/${encodeURIComponent(h.orderId)}`} className="underline underline-offset-2 hover:text-osmix-tangelo">
										{h.orderNumber ? `заказ №${h.orderNumber}` : "заказ"}
									</Link>
									{h.reverted && " · заказ отменён, код снова доступен"}
								</p>
							</div>
							<span className="text-[16px] leading-[22px] font-bold text-[#161616]">{h.amount > 0 ? `-${fmt(h.amount)}` : "-"}</span>
						</li>)}
				</ul>}
		</ProfilePanel>;
}